// apps/web/src/components/BarcodeProductPreview.tsx
import { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import { recomputeMacros } from "../lib/barcode";
import { WeightStepper } from "./WeightStepper";
import type { NormalizedProduct } from "@shared/types";

interface Props {
  product: NormalizedProduct;
  onChange: (amountG: number, macros: ReturnType<typeof recomputeMacros>, entryName: string) => void;
  onClear: () => void;
}

export function BarcodeProductPreview({ product, onChange, onClear }: Props) {
  const t = useT();
  const initialAmount = () =>
    product.serving_size_g && product.serving_size_g > 0 ? product.serving_size_g : 100;
  const [amount, setAmount] = useState<number>(initialAmount);
  const [unit, setUnit] = useState<"g" | "ml">(product.unit ?? "g");

  const entryName = product.brand ? `${product.brand} · ${product.name}` : product.name;

  // Single source of truth for pushing derived macros to the parent: set state AND
  // emit in the same call. Used by the stepper, the unit toggle, and the presets —
  // so a unit-only change still reaches the parent, and there is no effect race.
  function apply(amt: number, u: "g" | "ml") {
    setAmount(amt);
    setUnit(u);
    onChange(amt, recomputeMacros(product.per_100g, amt), entryName);
  }

  // On mount / when a different product is scanned: reset + emit the initial macros
  // once (no stale-amount transient — we emit the fresh initial amount directly).
  useEffect(() => {
    const init = initialAmount();
    setAmount(init);
    setUnit(product.unit ?? "g");
    onChange(init, recomputeMacros(product.per_100g, init), entryName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.code]);

  return (
    <div className="bg-surface rounded-2xl p-3 mb-3 flex gap-3 items-start">
      {product.image_url && (
        <img src={product.image_url} alt="" className="w-14 h-14 rounded-xl object-cover bg-surface" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{entryName}</div>
        <div className="text-sm text-fg-muted mt-0.5 tabular-nums">
          {product.per_100g.kcal} kcal/100 {unit} · P {product.per_100g.protein_g ?? "-"}g
          · C {product.per_100g.carbs_g ?? "-"}g
          · F {product.per_100g.fat_g ?? "-"}g
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <WeightStepper
            value={amount}
            unit={unit}
            onChange={(g, u) => apply(g, u)}
            max={2000}
            aria-label={t("barcode.amount_label")}
          />
          {product.serving_size_g ? (
            <button type="button" onClick={() => apply(product.serving_size_g!, unit)} className="text-sm bg-surface rounded-xl px-2 py-1">
              {t("barcode.amount_serving")}
            </button>
          ) : null}
          <button type="button" onClick={() => apply(100, unit)} className="text-sm bg-surface rounded-xl px-2 py-1">
            {t("barcode.amount_100unit", { unit })}
          </button>
          <button type="button" onClick={onClear} aria-label={t("common.close")} className="ml-auto text-sm text-fg-muted">✕</button>
        </div>
        <div className="text-sm text-fg-faint mt-1">
          {product.serving_size_g
            ? t("barcode.per_serving_unit_helper", { n: product.serving_size_g, unit })
            : t("barcode.per_100unit_helper", { unit })}
        </div>
      </div>
    </div>
  );
}
